import $ from "jquery";
import BD_Component from "@bd-core/bd-component";
import compile from "@bd-utils/handlerbars-helper";
import GameLogic from "@bd-core/game-logic";
import bdapi from "@bd-core/bd-api";
import bdjs from "@bd-core/bdjs";
import CONFIG from "@bd-core/config";
import { createInfoTooltipLayer } from "@bd-utils/info-tooltip";
import {
    Chart,
    LineController,
    LineElement,
    PointElement,
    LinearScale,
    CategoryScale,
    Filler,
    Tooltip,
} from "chart.js";

Chart.register(
    LineController,
    LineElement,
    PointElement,
    LinearScale,
    CategoryScale,
    Filler,
    Tooltip,
);

const OVERVIEW_CHART_ORANGE = "rgb(249, 115, 22)";

/**
 * @param {string} text
 */
function escapeHtmlForOverviewMetric(text) {
    return String(text)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
}

/**
 * Yesterday metric display: euro in `sup`, fractional digits in `sub`.
 * @param {unknown} raw
 * @param {string} [valueType]
 */
function formatOverviewYesterdayDisplay(raw, valueType) {
    if (raw == null || raw === "") {
        return "—";
    }
    const str = String(raw).trim();
    const dot = str.indexOf(".");
    const euro = valueType === "decimal" ? "" : "<sup>€</sup>";
    if (dot === -1) {
        return euro + escapeHtmlForOverviewMetric(str);
    }
    const intPart = str.slice(0, dot);
    const fracPart = str.slice(dot + 1);
    return (
        euro +
        escapeHtmlForOverviewMetric(intPart) +
        "<sub>." +
        escapeHtmlForOverviewMetric(fracPart) +
        "</sub>"
    );
}

/**
 * @typedef {import("chart.js").Chart & {
 *   $overviewActiveXIndex: number | null;
 * }} OverviewChart
 */

/** Vertical guide: from the data value down to the baseline (tooltip hover or clicked point). */
const overviewChartCrosshairPlugin = {
    id: "overviewChartCrosshair",
    /** @param {OverviewChart} chart */
    afterInit(chart) {
        chart.$overviewActiveXIndex = null;
    },
    /** @param {OverviewChart} chart */
    afterDatasetsDraw(chart) {
        const tooltip = chart.tooltip;
        const { ctx, chartArea, scales } = chart;
        if (!chartArea) {
            return;
        }
        const yScale = scales.y;
        let x = null;
        let yParsed = null;

        if (tooltip && tooltip.opacity > 0) {
            const caretX = tooltip.caretX;
            const items = tooltip.dataPoints;
            if (
                caretX != null &&
                Number.isFinite(caretX) &&
                Array.isArray(items) &&
                items.length
            ) {
                x = caretX;
                yParsed = items[0].parsed?.y;
            }
        } else if (
            chart.$overviewActiveXIndex != null &&
            Number.isFinite(chart.$overviewActiveXIndex)
        ) {
            const idx = chart.$overviewActiveXIndex;
            const meta = chart.getDatasetMeta(0);
            const pt = meta?.data?.[idx];
            const raw = chart.data?.datasets?.[0]?.data?.[idx];
            if (pt && raw != null && Number.isFinite(Number(raw))) {
                x = pt.x;
                yParsed = Number(raw);
            }
        }

        if (x == null || !Number.isFinite(x) || yScale == null) {
            return;
        }
        let yAtValue = null;
        if (yParsed != null && Number.isFinite(Number(yParsed))) {
            yAtValue = yScale.getPixelForValue(yParsed);
        }
        if (yAtValue == null || !Number.isFinite(yAtValue)) {
            return;
        }
        const yTop = Math.max(
            chartArea.top,
            Math.min(chartArea.bottom, yAtValue),
        );
        ctx.save();
        ctx.beginPath();
        ctx.moveTo(x, yTop);
        ctx.lineTo(x, chartArea.bottom);
        ctx.lineWidth = 2;
        ctx.strokeStyle = OVERVIEW_CHART_ORANGE;
        ctx.setLineDash([6, 5]);
        ctx.stroke();
        ctx.restore();
    },
};

/** Solid orange marker + white center + shadow for the clicked data point (drawn on top). */
const overviewChartSelectedPointPlugin = {
    id: "overviewChartSelectedPoint",
    /** @param {OverviewChart} chart */
    afterDatasetsDraw(chart) {
        const idx = chart.$overviewActiveXIndex;
        if (idx == null || !Number.isFinite(idx)) {
            return;
        }
        const raw = chart.data?.datasets?.[0]?.data?.[idx];
        if (raw == null || !Number.isFinite(Number(raw))) {
            return;
        }
        const meta = chart.getDatasetMeta(0);
        const pt = meta?.data?.[idx];
        if (!pt) {
            return;
        }
        const x = pt.x;
        const y = pt.y;
        const { ctx } = chart;
        const outerR = 7;
        const innerR = 2.5;

        ctx.save();
        ctx.shadowColor = "rgba(15, 23, 42, 0.3)";
        ctx.shadowBlur = 10;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 4;
        ctx.beginPath();
        ctx.arc(x, y, outerR, 0, Math.PI * 2);
        ctx.fillStyle = OVERVIEW_CHART_ORANGE;
        ctx.fill();
        ctx.restore();

        ctx.save();
        ctx.beginPath();
        ctx.arc(x, y, innerR, 0, Math.PI * 2);
        ctx.fillStyle = "#ffffff";
        ctx.fill();
        ctx.restore();
    },
};

export class OverviewPage extends BD_Component {
    constructor() {
        super();

        this.id = "overview-page";
        this.template = "overview-page-template";

        this.elms = {
            overviewPage: ".overview-page",
            overviewBody: ".overview-page__body",
            app: ".app",
            backToHomeButton: ".overview-page__header__back",
        };

        this.classes = {};

        this._events = this.getEvents();
        this._teamId = null;
        this._overviewData = {};
        /** @type {Record<string, Chart>} */
        this._chartsByMetric = {};
        /** @type {string[]} */
        this.defaultMetricOrder = ["opt", "apt", "omzet"];
        /** @type {{ opt: number, apt: number, omzet: number }} */
        this._metricChartOffsets = { opt: 0, apt: 0, omzet: 0 };
        this._chartMaxWeekOffset = 5;
        this._overviewChartNavPending = false;
        this.metricRowTemplate = "overview-metric-row-template";
        this._infoTooltip = null;
    }

    getEvents() {
        const elms = this.elms;

        return [
            {
                selector: elms.backToHomeButton,
                event: "click",
                handler: this.handleBackToHome,
            },
            {
                selector: ".overview-page__metric__chart-nav--prev",
                event: "click",
                handler: this.handleChartNavOlder,
            },
            {
                selector: ".overview-page__metric__chart-nav--next",
                event: "click",
                handler: this.handleChartNavNewer,
            },
        ];
    }

    beforeInit() {
        this._teamId = localStorage.getItem("teamId");
        this.render();
        this.setGameState();
    }

    async render() {
        this._teardownInfoTooltip();
        const data = this._data;
        if (!data.overview_page) {
            data.overview_page = {};
        }
        this.mergeOverviewPageChartNavCopy(data);

        const htmlTemplate = $("#" + this.template).html();

        data.overview_metrics_loading = true;
        data.overview_metrics = this.metricPlaceholders();

        let html = compile(htmlTemplate, data);
        $(this.elms.app).html(html);

        await this.getOverviewData(0);

        data.overview_metrics_loading = false;
        data.overview_metrics = this.shapeOverviewMetrics(this._overviewData);

        html = compile(htmlTemplate, data);
        $(this.elms.app).html(html);

        this._infoTooltip = createInfoTooltipLayer(
            "overview",
            $(this.elms.app),
        );
        this._infoTooltip.setTriggerText(
            $(this.elms.app).find(".overview-page__header__info"),
            CONFIG.game?.tooltip?.overview_page?.overview ?? "",
        );

        this._initOverviewCharts();
    }

    setGameState() {}

    _teardownInfoTooltip() {
        this._infoTooltip?.destroy();
        this._infoTooltip = null;
    }

    beforeDestroy() {
        this._teardownInfoTooltip();
        this.destroyCharts();
    }

    handleBackToHome(e) {
        e.preventDefault();
        GameLogic.goToPage("home-page");
    }

    /**
     * @param {{ overview_page?: Record<string, string> }} data
     */
    mergeOverviewPageChartNavCopy(data) {
        const page = data.overview_page || (data.overview_page = {});
        if (!page.chart_nav_group_label) {
            page.chart_nav_group_label = "Weekly chart range";
        }
        if (!page.chart_nav_older_label) {
            page.chart_nav_older_label = "Show older weeks";
        }
        if (!page.chart_nav_newer_label) {
            page.chart_nav_newer_label = "Show newer weeks";
        }
    }

    /**
     * @param {number | Record<string, number>} chartSpec Uniform offset (legacy) or per-metric offsets.
     * @param {{ preserveOnError?: boolean }} [options]
     * @returns {Promise<boolean>}
     */
    async getOverviewData(chartSpec = 0, options = {}) {
        const { preserveOnError = false } = options;
        const maxRaw = this._overviewData?.chart_max_week_offset;
        const max =
            maxRaw != null && Number.isFinite(Number(maxRaw))
                ? Number(maxRaw)
                : this._chartMaxWeekOffset;

        const clampOne = (v) =>
            Math.max(0, Math.min(max, Number(v) || 0));

        /** @type {Record<string, unknown>} */
        const payload = {
            teamId: this._teamId,
        };

        if (typeof chartSpec === "number") {
            const o = clampOne(chartSpec);
            payload.chartWeekOffset = o;
            payload.chart_week_offsets = { opt: o, apt: o, omzet: o };
        } else if (chartSpec && typeof chartSpec === "object") {
            const m = /** @type {Record<string, unknown>} */ (chartSpec);
            const offsets = {
                opt: clampOne(m.opt),
                apt: clampOne(m.apt),
                omzet: clampOne(m.omzet),
            };
            payload.chartWeekOffset = offsets.opt;
            payload.chart_week_offsets = offsets;
        } else {
            return false;
        }

        const [res, err] = await bdjs.goAsync(
            bdapi.post("game/get-overview-data", payload),
        );

        if (err || !res?.data?.success) {
            console.error("get-overview-data failed", err || res?.data);
            if (!preserveOnError) {
                this._overviewData = {};
            }
            return false;
        }

        this._overviewData = res?.data?.data ?? {};
        const cwo = this._overviewData.chart_week_offsets;
        if (cwo && typeof cwo === "object") {
            this._metricChartOffsets = {
                opt: Number(/** @type {{ opt?: unknown }} */ (cwo).opt) || 0,
                apt: Number(/** @type {{ apt?: unknown }} */ (cwo).apt) || 0,
                omzet:
                    Number(/** @type {{ omzet?: unknown }} */ (cwo).omzet) ||
                    0,
            };
        } else {
            const leg =
                Number(this._overviewData.chart_week_offset) || 0;
            this._metricChartOffsets = {
                opt: leg,
                apt: leg,
                omzet: leg,
            };
        }
        const mr = this._overviewData.chart_max_week_offset;
        if (mr != null && Number.isFinite(Number(mr))) {
            this._chartMaxWeekOffset = Number(mr);
        }
        return true;
    }

    handleChartNavOlder(e) {
        e.preventDefault();
        if (this._overviewChartNavPending) {
            return;
        }
        const raw = this._overviewData;
        if (!raw?.stats || typeof raw.stats !== "object") {
            return;
        }
        const metricKey = $(e.currentTarget)
            .closest("[data-metric]")
            .attr("data-metric");
        if (!metricKey || !this.defaultMetricOrder.includes(metricKey)) {
            return;
        }
        const maxRaw = raw.chart_max_week_offset;
        const max =
            maxRaw != null && Number.isFinite(Number(maxRaw))
                ? Number(maxRaw)
                : this._chartMaxWeekOffset;
        const cur = this._metricChartOffsets[metricKey] ?? 0;
        if (cur >= max) {
            return;
        }
        const nextOffsets = {
            ...this._metricChartOffsets,
            [metricKey]: cur + 1,
        };
        void this.refreshOverviewMetricRow(nextOffsets, metricKey);
    }

    handleChartNavNewer(e) {
        e.preventDefault();
        if (this._overviewChartNavPending) {
            return;
        }
        const raw = this._overviewData;
        if (!raw?.stats || typeof raw.stats !== "object") {
            return;
        }
        const metricKey = $(e.currentTarget)
            .closest("[data-metric]")
            .attr("data-metric");
        if (!metricKey || !this.defaultMetricOrder.includes(metricKey)) {
            return;
        }
        const cur = this._metricChartOffsets[metricKey] ?? 0;
        if (cur <= 0) {
            return;
        }
        const nextOffsets = {
            ...this._metricChartOffsets,
            [metricKey]: cur - 1,
        };
        void this.refreshOverviewMetricRow(nextOffsets, metricKey);
    }

    /**
     * @param {{ opt: number, apt: number, omzet: number }} nextOffsets
     * @param {string} metricKey
     */
    async refreshOverviewMetricRow(nextOffsets, metricKey) {
        if (this._overviewChartNavPending) {
            return;
        }
        this._overviewChartNavPending = true;
        const $root = $(this.elms.app);
        const $page = $root.find(this.elms.overviewPage);
        $page.addClass("overview-page--chart-nav-pending");

        try {
            const ok = await this.getOverviewData(nextOffsets, {
                preserveOnError: true,
            });
            if (!ok || !this._overviewData?.stats) {
                return;
            }

            const data = this._data;
            if (!data.overview_page) {
                data.overview_page = {};
            }
            this.mergeOverviewPageChartNavCopy(data);
            const metrics = this.shapeOverviewMetrics(this._overviewData);
            const rowData = metrics.find((m) => m.key === metricKey);
            if (!rowData) {
                return;
            }

            const rowTemplate = $("#" + this.metricRowTemplate).html();
            if (!rowTemplate) {
                return;
            }

            const ctx = $.extend({}, data, rowData, {
                overview_metrics_loading: false,
            });
            const html = compile(rowTemplate, ctx);

            const $row = $root.find(
                `[data-metric="${$.escapeSelector(metricKey)}"]`,
            );
            this.destroyChartForMetric(metricKey);
            $row.replaceWith(html);

            this._initOverviewCharts(metricKey);
        } finally {
            this._overviewChartNavPending = false;
            $root
                .find(this.elms.overviewPage)
                .removeClass("overview-page--chart-nav-pending");
        }
    }

    destroyCharts() {
        const charts = this._chartsByMetric;
        if (!charts || typeof charts !== "object") {
            return;
        }
        Object.keys(charts).forEach((k) => {
            charts[k]?.destroy();
        });
        this._chartsByMetric = {};
    }

    /**
     * @param {string} metricKey
     */
    destroyChartForMetric(metricKey) {
        const c = this._chartsByMetric[metricKey];
        if (c) {
            c.destroy();
            delete this._chartsByMetric[metricKey];
        }
    }

    metricPlaceholders() {
        return this.defaultMetricOrder.map((key) => ({
            key,
            title: key.toUpperCase(),
        }));
    }

    /**
     * @param {(number|null)[]} values
     * @param {{ paddingRatio?: number, minPadding?: number, clampMinToZero?: boolean }} opts
     */
    computeYRange(values, opts = {}) {
        const paddingRatio = opts.paddingRatio ?? 0.12;
        const minPadding = opts.minPadding ?? 0;
        const clampMinToZero = opts.clampMinToZero ?? false;

        const nums = values
            .filter((v) => v != null && Number.isFinite(Number(v)))
            .map((v) => Number(v));

        if (nums.length === 0) {
            return { min: 0, max: 1 };
        }

        let dataMin = Math.min(...nums);
        let dataMax = Math.max(...nums);

        if (dataMin === dataMax) {
            const base = Math.abs(dataMin) < 1e-12 ? 1 : Math.abs(dataMin);
            dataMin -= base * 0.1;
            dataMax += base * 0.1;
        }

        const range = dataMax - dataMin;
        const padding = Math.max(range * paddingRatio, minPadding);
        let yMin = dataMin - padding;
        let yMax = dataMax + padding;

        if (clampMinToZero && yMin < 0) {
            yMin = 0;
        }
        if (yMax <= yMin) {
            yMax = yMin + 1;
        }

        return { min: yMin, max: yMax };
    }

    /**
     * @param {Record<string, unknown>} raw
     */
    shapeOverviewMetrics(raw) {
        const order =
            Array.isArray(raw?.order) && raw.order.length
                ? raw.order
                : this.defaultMetricOrder;
        const stats =
            raw?.stats && typeof raw.stats === "object" ? raw.stats : {};
        const maxOffset =
            raw.chart_max_week_offset != null &&
            Number.isFinite(Number(raw.chart_max_week_offset))
                ? Number(raw.chart_max_week_offset)
                : this._chartMaxWeekOffset;
        const cwoRaw = raw.chart_week_offsets;

        return order.map((key) => {
            const s = /** @type {Record<string, unknown>} */ (stats[key] || {});
            const wow = s.week_over_week_percent;
            let weekChangeDirection = "none";
            if (wow == null) {
                weekChangeDirection = "none";
            } else if (Number(wow) > 0) {
                weekChangeDirection = "up";
            } else if (Number(wow) < 0) {
                weekChangeDirection = "down";
            } else {
                weekChangeDirection = "flat";
            }

            const weeks = Array.isArray(s.chart_weeks) ? s.chart_weeks : [];
            const chartLabels = weeks.map((w) =>
                String(/** @type {{ iso_week?: number }} */ (w).iso_week ?? ""),
            );
            const chartValues = weeks.map((w) =>
                /** @type {{ value?: number | null }} */ (w).value == null
                    ? null
                    : Number(
                          /** @type {{ value?: number | null }} */ (w).value,
                      ),
            );

            const wowNum = wow == null ? null : Number(wow);
            const weekPercentDisplay =
                wowNum == null || Number.isNaN(wowNum)
                    ? "—"
                    : `${wowNum > 0 ? "+" : ""}${wowNum}%`;

            let thisOff = 0;
            if (cwoRaw && typeof cwoRaw === "object") {
                const v = /** @type {Record<string, unknown>} */ (cwoRaw)[
                    key
                ];
                thisOff = Number(v) || 0;
            } else {
                thisOff = Number(raw.chart_week_offset) || 0;
            }

            const valueType = s.value_type === "decimal" ? "decimal" : "money";

            return {
                key,
                title: String(key).toUpperCase(),
                chart_can_go_older: thisOff < maxOffset,
                chart_can_go_newer: thisOff > 0,
                value_type: valueType,
                yesterday: formatOverviewYesterdayDisplay(s.yesterday, valueType),
                week_percent_display: weekPercentDisplay,
                week_change_direction: weekChangeDirection,
                tooltips: {
                    yesterday: String(
                        /** @type {{ tooltips?: { yesterday?: string } }} */ (s)
                            .tooltips?.yesterday ?? "",
                    ),
                    week_comparison: String(
                        /** @type {{ tooltips?: { week_comparison?: string } }} */ (
                            s
                        ).tooltips?.week_comparison ?? "",
                    ),
                    weekly_chart: String(
                        /** @type {{ tooltips?: { weekly_chart?: string } }} */ (
                            s
                        ).tooltips?.weekly_chart ?? "",
                    ),
                },
                chart_labels_json: JSON.stringify(chartLabels),
                chart_values_json: JSON.stringify(chartValues),
            };
        });
    }

    /**
     * @param {HTMLCanvasElement} canvas
     * @param {string[]} labels
     * @param {(number|null)[]} values
     * @param {string} valueType
     */
    createOverviewChart(canvas, labels, values, valueType) {
        const numericValues = values.map((v) => {
            if (v == null) {
                return null;
            }
            const n = Number(v);
            return Number.isNaN(n) ? null : n;
        });

        const { min: yMin, max: yMax } = this.computeYRange(numericValues, {
            clampMinToZero: valueType === "money",
        });

        const formatEuro = (v) => {
            const n = Number(v);
            if (!Number.isFinite(n)) {
                return "€0";
            }
            if (Number.isInteger(n)) {
                return `€${n}`;
            }
            return `€${n.toFixed(2)}`;
        };

        const formatDecimal = (v) => {
            const n = Number(v);
            if (!Number.isFinite(n)) {
                return "0";
            }
            return n % 1 === 0 ? String(n) : n.toFixed(2);
        };

        const lineColor = OVERVIEW_CHART_ORANGE;
        const fillGradient = (context) => {
            const chart = context.chart;
            const { ctx, chartArea } = chart;
            if (!chartArea) {
                return "rgba(249, 115, 22, 0.12)";
            }
            const g = ctx.createLinearGradient(
                0,
                chartArea.top,
                0,
                chartArea.bottom,
            );
            g.addColorStop(0, "rgba(249, 115, 22, 0.35)");
            g.addColorStop(0.55, "rgba(249, 115, 22, 0.08)");
            g.addColorStop(1, "rgba(249, 115, 22, 0)");
            return g;
        };

        return new Chart(canvas, {
            type: "line",
            data: {
                labels,
                datasets: [
                    {
                        label: "Weekly",
                        data: numericValues,
                        fill: true,
                        backgroundColor: fillGradient,
                        borderColor: lineColor,
                        borderWidth: 3,
                        tension: 0.4,
                        pointRadius: 0,
                        pointHoverRadius: (ctx) => {
                            const c = /** @type {OverviewChart} */ (ctx.chart);
                            if (c.$overviewActiveXIndex === ctx.dataIndex) {
                                return 0;
                            }
                            return 6;
                        },
                        pointHitRadius: 12,
                        spanGaps: true,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                onClick: (e, elements, chart) => {
                    const ch = /** @type {OverviewChart} */ (chart);
                    if (elements.length) {
                        ch.$overviewActiveXIndex = elements[0].index;
                    } else {
                        ch.$overviewActiveXIndex = null;
                    }
                    ch.update();
                },
                interaction: {
                    mode: "index",
                    intersect: false,
                    axis: "x",
                },
                plugins: {
                    legend: { display: false },
                    tooltip: {
                        enabled: true,
                        mode: "index",
                        intersect: false,
                        axis: "x",
                        backgroundColor: "#242323",
                        cornerRadius: 8,
                        caretSize: 7,
                        padding: {
                            top: 8,
                            right: 10,
                            bottom: 8,
                            left: 10,
                        },
                        titleFont: {
                            size: 16,
                            weight: 600,
                        },
                        bodyFont: {
                            size: 10,
                            weight: 400,
                        },
                        titleMarginBottom: 2,
                        bodySpacing: 2,
                        titleAlign: "right",
                        bodyAlign: "right",
                        footerAlign: "right",
                        displayColors: false,
                        callbacks: {
                            title: (items) => {
                                if (!items.length) {
                                    return "";
                                }
                                const v = items[0].parsed?.y;
                                if (v == null || Number.isNaN(Number(v))) {
                                    return "—";
                                }
                                return valueType === "money"
                                    ? formatEuro(v)
                                    : formatDecimal(v);
                            },
                            label: (ctx) => String(ctx.label ?? ""),
                        },
                    },
                },
                scales: {
                    x: {
                        grid: { display: false },
                        border: { display: false },
                        ticks: {
                            maxTicksLimit: 7,
                            autoSkip: false,
                            callback: (tickValue, index) => {
                                return String(labels[index] ?? "");
                            },
                            /**
                             * @param {import("chart.js").ScriptableScaleContext} tickCtx
                             */
                            font: (tickCtx) => {
                                const chart = /** @type {OverviewChart} */ (
                                    tickCtx.chart
                                );
                                const active = chart.$overviewActiveXIndex;
                                const idx = tickCtx.index;
                                const base = { size: 11 };
                                if (active != null && active === idx) {
                                    return { ...base, weight: "bold" };
                                }
                                return { ...base, weight: "normal" };
                            },
                        },
                    },
                    y: {
                        min: yMin,
                        max: yMax,
                        ticks: {
                            maxTicksLimit: 7,
                            callback: (tickValue, index) => {
                                if (index === 0) {
                                    return "";
                                }
                                const v = Number(tickValue);
                                return valueType === "money"
                                    ? formatEuro(v)
                                    : formatDecimal(v);
                            },
                        },
                        grid: {
                            color: "#e2e9f2",
                        },
                        border: { 
                            display: false,
                            dash: [4, 4],
                            dashOffset: 4,
                        },
                    },
                },
            },
            plugins: [
                overviewChartCrosshairPlugin,
                overviewChartSelectedPointPlugin,
            ],
        });
    }

    /**
     * @param {string} [metricKey] When set, only (re)initializes that metric's chart.
     */
    _initOverviewCharts(metricKey) {
        if (metricKey) {
            this.destroyChartForMetric(metricKey);
        } else {
            this.destroyCharts();
        }

        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                if (metricKey) {
                    this.populateMetricChart(metricKey);
                    this._chartsByMetric[metricKey]?.resize();
                } else {
                    this.populateOverview();
                    Object.values(this._chartsByMetric).forEach((c) => c.resize());
                }
            });
        });
    }

    populateOverview() {
        this.destroyCharts();

        const $root = $(this.elms.app);
        $root.find(".overview-page__metric__canvas").each((_, el) => {
            const $canvas = $(el);
            const mk = $canvas.attr("data-metric-key");
            if (!mk) {
                return;
            }
            let labels = [];
            let values = [];
            try {
                labels = JSON.parse($canvas.attr("data-chart-labels") || "[]");
                values = JSON.parse($canvas.attr("data-chart-values") || "[]");
            } catch (e) {
                console.error("overview chart data parse error", e);
                return;
            }
            const valueType = $canvas.attr("data-value-type") || "money";
            const chart = this.createOverviewChart(
                /** @type {HTMLCanvasElement} */ (el),
                labels,
                values,
                valueType,
            );
            this._chartsByMetric[mk] = chart;
        });
    }

    /**
     * @param {string} metricKey
     */
    populateMetricChart(metricKey) {
        const $root = $(this.elms.app);
        const $canvas = $root.find(
            `[data-metric="${$.escapeSelector(metricKey)}"] .overview-page__metric__canvas`,
        );
        const el = $canvas[0];
        if (!el) {
            return;
        }
        let labels = [];
        let values = [];
        try {
            labels = JSON.parse($canvas.attr("data-chart-labels") || "[]");
            values = JSON.parse($canvas.attr("data-chart-values") || "[]");
        } catch (e) {
            console.error("overview chart data parse error", e);
            return;
        }
        const valueType = $canvas.attr("data-value-type") || "money";
        const chart = this.createOverviewChart(
            /** @type {HTMLCanvasElement} */ (el),
            labels,
            values,
            valueType,
        );
        this._chartsByMetric[metricKey] = chart;
    }
}